import React, { useEffect, useMemo } from "react";
import { Check, Loader2, ShieldAlert, ShieldCheck, X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { fetchRoleResourcePermissionViews } from "../../features/rbac/RBACSlice";
import { SectionCard } from "./shared/SectionCard";
import { StatusPill } from "./shared/StatusPill";

interface CurrentSessionUser {
  userId?: number;
  email?: string;
  name?: string;
  roleId?: number;
  resourceId?: number;
  subRole?: string;
  roleResourceId?: number;
}

const readSessionUser = (): CurrentSessionUser => {
  try {
    return JSON.parse(localStorage.getItem("userDetails") || "{}") as CurrentSessionUser;
  } catch {
    return {};
  }
};

const ROLE_LABELS: Record<number, string> = {
  1: "Admin",
  5: "Principal",
};

/**
 * Top-of-page diagnostic showing the currently logged-in user and their
 * effective admin-portal permission matrix. Helps explain "You don't have
 * enough permission to perform this action" errors when the backend
 * `grantAccess` middleware rejects RBAC writes.
 */
export const MyAccessCard: React.FC = () => {
  const dispatch = useAppDispatch();
  const me = useMemo(readSessionUser, []);
  const { permissionViews, resources, viewsLoading } = useAppSelector((s) => s.rbac);

  const roleResourceId = me.roleResourceId;
  const loadedForMe =
    roleResourceId != null && permissionViews.roleResourceId === roleResourceId;

  useEffect(() => {
    if (roleResourceId != null && !loadedForMe && !viewsLoading) {
      dispatch(fetchRoleResourcePermissionViews(roleResourceId));
    }
  }, [dispatch, roleResourceId, loadedForMe, viewsLoading]);

  const matrix = loadedForMe ? permissionViews.adminPortal : null;

  const adminLike = me.roleId === 1;

  const resourceNameById = useMemo(
    () => new Map(resources.map((r) => [r.resourceId, r.resourceName])),
    [resources]
  );

  const matrixEntries = matrix ? Object.entries(matrix) : [];
  const findEntry = (predicate: (name: string) => boolean) =>
    matrixEntries.find(([rid]) => {
      const name = resourceNameById.get(Number(rid));
      return name ? predicate(name) : false;
    });

  const roleResourcePermissionAccess = findEntry((n) =>
    /role[\s_-]?resource[\s_-]?permission/i.test(n)
  )?.[1];
  const roleResourceAccess = findEntry((n) => /role[\s_-]?resource/i.test(n))?.[1];
  const permissionAccess = findEntry((n) => /^permission(s)?$/i.test(n))?.[1];

  const canCreateMappings = adminLike || Boolean(roleResourcePermissionAccess?.create);

  return (
    <SectionCard
      title={
        <span className="flex items-center gap-2">
          {canCreateMappings ? (
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
          ) : (
            <ShieldAlert className="h-5 w-5 text-amber-600" />
          )}
          My access
        </span>
      }
      description={
        canCreateMappings
          ? "Your account can perform RBAC writes."
          : "Your account may not have permission to perform RBAC writes. The backend grantAccess middleware will reject create/update/delete on RBAC resources unless your role-resource has these permissions."
      }
      actions={
        roleResourceId != null && (
          <button
            type="button"
            onClick={() => dispatch(fetchRoleResourcePermissionViews(roleResourceId))}
            disabled={viewsLoading}
            className="inline-flex items-center gap-2 rounded-md border border-cardborder bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {viewsLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Recheck permissions
          </button>
        )
      }
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-1 text-sm">
          <div>
            <span className="text-gray-500">User: </span>
            <span className="font-medium text-gray-900">
              {me.name?.trim() || me.email || `#${me.userId ?? "?"}`}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Email: </span>
            <span className="text-gray-800">{me.email ?? "—"}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-gray-500">Role:</span>
            <StatusPill variant={adminLike ? "success" : "info"}>
              {ROLE_LABELS[me.roleId ?? 0] ?? `roleId ${me.roleId ?? "?"}`}
            </StatusPill>
            {me.subRole && <StatusPill variant="neutral">{me.subRole}</StatusPill>}
          </div>
          <div>
            <span className="text-gray-500">Role-resource: </span>
            <span className="font-mono text-xs text-gray-700">
              {me.roleResourceId ?? "—"}
            </span>
          </div>
        </div>

        <div className="rounded-md border border-cardborder bg-gray-50 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Effective permissions on RBAC resources
          </div>
          {viewsLoading && !matrix ? (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin text-brand" />
              Loading permission matrix…
            </div>
          ) : adminLike ? (
            <div className="text-sm text-emerald-700">
              Admin role detected (roleId 1). Full RBAC privileges expected.
            </div>
          ) : !matrix ? (
            <div className="text-sm text-gray-500">
              Matrix not loaded for your role-resource yet.
            </div>
          ) : (
            <div className="space-y-1 text-sm">
              <AccessLine label="role-resource-permission" value={roleResourcePermissionAccess} />
              <AccessLine label="role-resource" value={roleResourceAccess} />
              <AccessLine label="permission" value={permissionAccess} />
            </div>
          )}
          {!adminLike && !canCreateMappings && (
            <p className="mt-3 text-xs text-amber-700">
              Tip: ask an admin to grant <code className="rounded bg-white px-1">create</code>{" "}
              on the <code className="rounded bg-white px-1">roleresourcepermission</code>{" "}
              resource for your role-resource (#{me.roleResourceId ?? "?"}), or sign in with an
              admin account to perform RBAC writes.
            </p>
          )}
        </div>
      </div>
    </SectionCard>
  );
};

const AccessLine: React.FC<{
  label: string;
  value: { read?: boolean; create?: boolean; update?: boolean; delete?: boolean } | undefined;
}> = ({ label, value }) => {
  if (!value) {
    return (
      <div className="flex items-center justify-between">
        <span className="text-gray-700">{label}</span>
        <span className="text-xs text-gray-400">no entry</span>
      </div>
    );
  }
  const cell = (v?: boolean) =>
    v ? (
      <Check className="h-3.5 w-3.5 text-emerald-600" />
    ) : (
      <X className="h-3.5 w-3.5 text-gray-300" />
    );
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-gray-700">{label}</span>
      <div className="flex items-center gap-3 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1">R{cell(value.read)}</span>
        <span className="inline-flex items-center gap-1">C{cell(value.create)}</span>
        <span className="inline-flex items-center gap-1">U{cell(value.update)}</span>
        <span className="inline-flex items-center gap-1">D{cell(value.delete)}</span>
      </div>
    </div>
  );
};
